<template>
	<ui-animate name="jump">
		<ui-bottom-bar v-if="settings.showBar" id="city-user-bar" class="bottom-bar animation-footer">
			<template #header>
				<ui-inline class="show-in-desktop">
					<TransitionGroup name="fade-inline">
						<ui-button v-for="btn in buttons" :key="btn" :data-id="btn['data-id']" v-tap="() => btn?.action()" :slim="true" color="dark-green">{{ btn?.name }}</ui-button>
					</TransitionGroup>
				</ui-inline>
			</template>
			
			<div class="flex gap-2">
				<ui-animate>
					<ui-icon-button
						v-if="settings.showSoundSettingsBtn"
						id="settings-button"
						icon="audio-on"
						data-id="sound-btn"
						v-tap="() => openSoundSettings()"
					/>
				</ui-animate>
						
				<ui-animate>
					<ui-icon-button
						data-id="show-user-btn"
						v-if="settings.showUserBtn"
						:icon="settings.grayOutUserIconBtn ? 'disconnected-user-icon' : 'connected-user-icon' "
						v-tap="() => openUserSettings()"
					/>
				</ui-animate>
			</div>
			
			<ui-inline v-if="buttons?.length > 0" class="show-in-mobile mx-auto w-full" :class="{ 'mr-3' : settings?.showSoundSettingsBtn }">
				<TransitionGroup name="fade-inline">
					<ui-button v-for="btn in buttons" :key="btn" v-tap="() => btn?.action()" :slim="true" color="dark-green">{{ btn?.name }}</ui-button>
				</TransitionGroup>
			</ui-inline>

			<ui-animate>
				<ui-vertical-separator v-if="settings.showLeftSeparator"/>
			</ui-animate>

			<div class="ml-2 show-in-desktop"></div>

			<ui-animate>
				<div v-if="settings.showJournal || settings.showJournalSlot" class="journal" v-tap="onOpenJournal">
					<ui-animate>
						<ui-journal-button :cover="journal?.cover" :key="journal?.cover" v-if="settings?.showJournal" ref="journalRef" class="journal" v-tap="onOpenJournal" />
					</ui-animate>
				</div>
			</ui-animate>

			<ui-animate>
				<ui-suitcase-button v-if="settings?.showSuitcase && showSuitcase" ref="suitcaseRef" class="-left-2" v-tap="onOpenSuitcase" />
			</ui-animate>
	
			<div class="ml-auto"></div>
			<ui-animate>
				<ui-icon-button
					v-if="settings.showHomeBtn"
					:class="{ '-mobile-mr-3' : settings?.showGameMenuBtn }"
					icon="home.png"
					:size="34"
					data-id="home-btn"
					v-tap="() => goToRoute('home')"
				/>
			</ui-animate>

			<transition name="popup-difficulty">
				<div v-if="hintBubbleVisible && !isGameMenuOpen" class="popup-difficulty hint-bubble">
					<div class="popup-difficulty-bg" v-tap="closeHint"></div>

					<div class="popup-difficulty-content">
						<div class="arrow" v-bg="'character-talk/bubble-arrow.svg'"></div>
						<div class="hint-text">Appuie sur une des <br/>zones pour jouer!</div>
					</div>
				</div>
			</transition>

			<ui-animate>
				<game-menu v-if="settings.showGameMenuBtn"></game-menu>
			</ui-animate>
		</ui-bottom-bar>
	</ui-animate>
	<Teleport to="body">
		<ui-icon-button
				v-if="settings.showSoundSettingsBtn && showFloatingSoundBtn"
				class="floating-settings-button"
				icon="audio-on"
				data-id="sound-btn-floating"
				v-tap="openSoundSettings"
		/>
	</Teleport>
</template>

<script setup>
import { storeToRefs } from 'pinia'
import { useRouter, useRoute } from 'vue-router'
import { useAppStore } from "@/stores/app-store"
import { useBottomBarManager } from "@/composable/bottom-bar-manager"
import { useUserStore } from '@/stores/user-store'
import { useMapStore } from '@/stores/map-store'
import { useGameStore } from '@/stores/game-store.js'
import { useTutoStore } from '@/stores/tuto-store'
import { ref, computed, watchEffect } from 'vue'
import UiIconButton from '@/components/_global/ui/ui-icon-button.vue'
import UiAnimate from '@/components/_global/animations/ui-animate.vue'
import UiBottomBar from '@/components/_global/ui/ui-bottom-bar.vue'

const { 
	settings,
	journal,
	buttons,
	openSuitcase, 
	openJournal,
	openSoundSettings,
	openUserSettings,
	goToRoute
} = useBottomBarManager({ useRouter, useUserStore, useMapStore, useAppStore, useTutoStore, useGameStore })

const journalRef = ref(null)
const suitcaseRef = ref(null)
const showSuitcase = ref(true)

const route = useRoute()
const appStore = useAppStore()
const gameStore = useGameStore()

const showFloatingSoundBtn = computed(() =>
		route.name === 'game' &&
		!!gameStore.gameId &&
		appStore.device !== 'mobile' &&
		!appStore.modals?.length
)

const { hintBubbleVisible, isGameMenuOpen } = storeToRefs(appStore)
const closeHint = () => appStore.hideHintBubble()

watchEffect(() => {
	if (journal?.value) {
		const journalData = journal?.value?.suitcase
		showSuitcase.value = journalData !== "none"
	}
})

const onOpenJournal 	= () => openJournal(journalRef?.value?.instance)
const onOpenSuitcase 	= () => openSuitcase(suitcaseRef?.value?.instance)
</script>

<style lang="stylus" scoped>

@import "../../assets/css/journal-button.styl"

.bottom-bar
	z-index 1
	.flex	
		#settings-button
			z-index 2

.floating-settings-button
	position fixed
	left 32px
	bottom calc(13px + env(safe-area-inset-bottom))
	z-index 2
	pointer-events auto

.show-in-mobile
	display none

.fade-inline-enter-active
	transition 0.35s easeOutBack 0.5s
	pointer-events none

.fade-inline-leave-active
	transition 0.35s easeInBack 0s
	pointer-events none

.fade-inline-enter-from
	opacity 0
	transform translateX(64px)
.fade-inline-leave-to
	opacity 0
	transform translateX(-64px)

.popup-difficulty
	fixed 0
	z-index 10
	pointer-events none
	&.popup-difficulty-enter-active
		transition 0.2s easeOutQuart
		.popup-difficulty-content
			transition 0.2s easeOutBack
	&.popup-difficulty-enter-from
		.popup-difficulty-content
			transform translate(16px, 16px) rotate(4deg)
			opacity 0
	&.popup-difficulty-leave-active
		transition 0.2s easeInQuart
		.popup-difficulty-content
			transition 0.2s easeInQuart
	&.popup-difficulty-leave-to
		.popup-difficulty-content
			transform translate(-16px, 16px) rotate(-4deg)
			opacity 0

	.popup-difficulty-bg
		absolute 0
		pointer-events none
	.popup-difficulty-content
		position absolute
		right 128px
		bottom 136px
		width 340px
		text-align center
		background-color #fff
		border-radius 16px
		padding 16px 20px
		box-shadow inset 0 0 0 1px #888, 0 0 8px alpha(#000, 50%)
		transform-origin bottom center
		pointer-events auto
		.arrow
			position absolute
			top calc(100% - 1px)
			left 70%
			width 64px
			height 64px
			background left top no-repeat
			background-size contain
	.hint-text
		font-size 2rem
		line-height 1.4em
		color dark

#mobile
	.show-in-mobile
		display block
		flex center center
		gap 8px
		left 4px
		.ui-button
			white-space nowrap
			height auto
			padding 6px 16px
			font-size 1.6rem
		
	.show-in-desktop
		display none

	.popup-difficulty
		.popup-difficulty-content
			right 16px
			bottom 160px

</style>
